// No 'use client': hooks are called from client components; server pages
// import the keys for prefetch wiring.

import {
  infiniteQueryOptions,
  queryOptions,
  useQuery,
  useInfiniteQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { ReactionKind } from '@leanin/shared/reactions';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';
import { QUERY_KEY_REGISTRY } from './normalize';

export type FeedScope =
  | 'all'
  | 'global'
  | 'circle'
  | 'network'
  | 'saved'
  | `group:${string}`;

export type FeedItemView = components['schemas']['FeedItemView'];
export type FeedItemPage = components['schemas']['PostsPage'];
export type FeedScopeCounts = components['schemas']['FeedScopeCounts'];
export type AuthoredPostView = components['schemas']['AuthoredPostView'];
export type PostView = components['schemas']['PostView'];

export type FeedListParams = {
  scope: FeedScope;
  pageSize?: number;
};

// ---- Client fetchers ----

// Every scope is a server-side filter — no client-side post-fetch slicing.
// Client filtering broke pagination: requesting N posts and discarding M of
// them shrinks the page silently and leaves the cursor pointing past the
// content that was thrown away.
//   'all'          → no filter (default)
//   'global'       → posts with is_global = true
//   'circle'       → 'kind:circle' (any circle the viewer is a member of)
//   'network'      → 'kind:network' (any network the viewer is a member of)
//   'group:<id>'   → a specific group; server enforces membership
//   'saved'        → savedByMe=true (orthogonal to scope on the server)
function buildListQuery(
  params: FeedListParams & { cursor?: string },
): {
  scope?: string;
  savedByMe?: 'true' | 'false';
  cursor?: string;
  pageSize?: number;
} {
  const q: {
    scope?: string;
    savedByMe?: 'true';
    cursor?: string;
    pageSize?: number;
  } = {};
  if (params.scope === 'saved') {
    q.savedByMe = 'true';
  } else if (params.scope === 'circle') {
    q.scope = 'kind:circle';
  } else if (params.scope === 'network') {
    q.scope = 'kind:network';
  } else if (params.scope !== 'all') {
    q.scope = params.scope;
  }
  if (params.cursor !== undefined) q.cursor = params.cursor;
  if (params.pageSize !== undefined) q.pageSize = params.pageSize;
  return q;
}

export async function fetchFeed(
  params: FeedListParams & { cursor?: string },
): Promise<FeedItemPage> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/posts', {
    params: { query: buildListQuery(params) },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchFeedScopeCounts(): Promise<FeedScopeCounts> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/posts/scope-counts');
  if (data === undefined) throwApiError(error);
  return data;
}

// Seen-post beacon: reports the post ids that scrolled through the viewer's
// viewport so the next feed load can decay their relevance. No-content (204)
// on success — nothing to return.
export async function reportImpressions(postIds: string[]): Promise<void> {
  if (postIds.length === 0) return;
  const client = getApiClient();
  const { error, response } = await client.POST('/api/v1/posts/impressions', {
    body: { postIds },
  });
  if (error !== undefined) throwApiError(error, response);
}

export async function fetchFeedPost(postId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/posts/{id}', {
    params: { path: { id: postId } },
  });
  if (response.status === 404) return null;
  if (data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchPostsByAuthor(
  authorId: string,
  limit?: number,
): Promise<readonly AuthoredPostView[]> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/members/{userId}/posts',
    {
      params: {
        path: { userId: authorId },
        query: limit !== undefined ? { limit } : {},
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data.items;
}

export type AuthorPostsListParams = {
  authorId: string;
  pageSize?: number;
};

export type AuthoredPostsPage = {
  items: readonly AuthoredPostView[];
  nextCursor: string | null;
};

// Cursor-paginated read of one member's authored posts, mirroring `fetchFeed`'s
// keyset shape so the profile activity list can grow past the first page with a
// "Load more" button (see the network-members roster affordance).
//
// Keyset-paginated: GET /members/{userId}/posts accepts `cursor` + `pageSize`
// and returns `{ items, nextCursor }` (same keyset contract as the feed).
export async function fetchPostsByAuthorPage(
  params: AuthorPostsListParams & { cursor?: string },
): Promise<AuthoredPostsPage> {
  const client = getApiClient();
  const query: { cursor?: string; pageSize?: number } = {};
  if (params.cursor !== undefined) query.cursor = params.cursor;
  if (params.pageSize !== undefined) query.pageSize = params.pageSize;
  const { data, error } = await client.GET('/api/v1/members/{userId}/posts', {
    params: { path: { userId: params.authorId }, query },
  });
  if (data === undefined) throwApiError(error);
  return { items: data.items, nextCursor: data.nextCursor ?? null };
}

export async function fetchFeedComments(args: {
  postId: string;
  pageSize?: number;
  cursor?: string | null;
}) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/posts/{id}/comments',
    {
      params: {
        path: { id: args.postId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor ?? undefined,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchFeedComment(commentId: string) {
  const client = getApiClient();
  // `response` makes a 404 throw NotFoundApiError, which the query client's
  // retry predicate treats as permanent — a dead deep-link id must not spin
  // through retries.
  const { data, error, response } = await client.GET(
    '/api/v1/posts/comments/{id}',
    {
      params: { path: { id: commentId } },
    },
  );
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

// Oldest-first page of one top-level comment's replies; the cursor walks
// toward newer replies.
export async function fetchFeedCommentReplies(args: {
  commentId: string;
  pageSize?: number;
  cursor?: string | null;
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/posts/comments/{id}/replies',
    {
      params: {
        path: { id: args.commentId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor ?? undefined,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error, response);
  return data;
}

// A "who reacted" roster exists for both posts and comments; the target
// discriminates which endpoint serves the page (identical `ReactorPage`
// contract on both).
export type ReactionTarget =
  | { kind: 'post'; id: string }
  | { kind: 'comment'; id: string };

export async function fetchFeedReactions(args: {
  target: ReactionTarget;
  kind?: ReactionKind;
  pageSize?: number;
  cursor?: string | null;
}) {
  const client = getApiClient();
  const params = {
    params: {
      path: { id: args.target.id },
      query: {
        kind: args.kind,
        pageSize: args.pageSize,
        cursor: args.cursor ?? undefined,
      },
    },
  };
  const { data, error, response } =
    args.target.kind === 'post'
      ? await client.GET('/api/v1/posts/{id}/reactions', params)
      : await client.GET('/api/v1/posts/comments/{id}/reactions', params);
  if (data === undefined) throwApiError(error, response);
  return data;
}

// ---- Query keys + leaf factories ----
//
// Inner nodes (`all`, `lists`, `authorPostsAll`) are bare `as const` prefix
// arrays used by the mutation layer for partial-match invalidation; they are
// NOT leaves. Every leaf returns a queryOptions/infiniteQueryOptions object
// built from one normalized `args`.

export const feedKeys = {
  all: ['feed'] as const,
  lists: () => [...feedKeys.all, 'list'] as const,

  // Infinite list leaf — cursor via pageParam, never in the key. The scope/
  // pageSize args are the terminal element; buildListQuery (the fetch-only
  // wire mapping) runs in the queryFn, not the key.
  list: (params: FeedListParams) =>
    infiniteQueryOptions({
      queryKey: [...feedKeys.lists(), params] as const,
      queryFn: ({ pageParam }) =>
        fetchFeed({ ...params, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: [...feedKeys.all, 'detail', id] as const,
      queryFn: () => fetchFeedPost(id),
    }),

  scopeCounts: () =>
    queryOptions({
      queryKey: [...feedKeys.all, 'scope-counts'] as const,
      queryFn: fetchFeedScopeCounts,
    }),

  // authorPosts leaf — authorId is a key id-segment, { limit } the terminal
  // args object.
  authorPosts: (authorId: string, limit?: number) =>
    queryOptions({
      queryKey: [...feedKeys.all, 'author', authorId, { limit }] as const,
      queryFn: () => fetchPostsByAuthor(authorId, limit),
    }),

  // Infinite author-posts leaf — authorId is a key id-segment, { pageSize }
  // the terminal args object; the cursor rides pageParam, never the key.
  // Mirrors `feedKeys.list` for the member-profile activity list's "Load more".
  authorPostsInfinite: (authorId: string, args?: { pageSize?: number }) =>
    infiniteQueryOptions({
      queryKey: [
        ...feedKeys.all,
        'author',
        authorId,
        'infinite',
        { pageSize: args?.pageSize },
      ] as const,
      queryFn: ({ pageParam }) =>
        fetchPostsByAuthorPage({
          authorId,
          pageSize: args?.pageSize,
          cursor: pageParam ?? undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  // Predicate prefix matching every author-posts cache (flat + infinite),
  // used by the mutation layer to walk and optimistically patch posts the
  // viewer is currently looking at on a profile page.
  authorPostsAll: () => [...feedKeys.all, 'author'] as const,

  comments: (postId: string) =>
    infiniteQueryOptions({
      queryKey: [...feedKeys.all, 'comments', postId] as const,
      queryFn: ({ pageParam }) =>
        fetchFeedComments({
          postId,
          cursor: (pageParam as string | undefined) ?? undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        (lastPage as { nextCursor?: string | null }).nextCursor ?? undefined,
    }),

  // Single-comment read — used by the post detail deep link (`?comment=`) to
  // learn a target comment's parent before the thread renders.
  commentDetail: (commentId: string) =>
    queryOptions({
      queryKey: [...feedKeys.all, 'comment-detail', commentId] as const,
      queryFn: () => fetchFeedComment(commentId),
    }),

  // Predicate prefix matching every reply-thread cache, used by the mutation
  // layer to optimistically patch a reply wherever its thread is loaded
  // (the parent comment id isn't in the mutation's vars).
  commentRepliesAll: () => [...feedKeys.all, 'comment-replies'] as const,

  // Infinite reply-thread leaf — oldest-first, cursor rides pageParam.
  commentReplies: (commentId: string) =>
    infiniteQueryOptions({
      queryKey: [...feedKeys.commentRepliesAll(), commentId] as const,
      queryFn: ({ pageParam }) =>
        fetchFeedCommentReplies({
          commentId,
          cursor: (pageParam as string | undefined) ?? undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  // Infinite who-reacted leaf — the target's kind+id are key id-segments,
  // { kind } the terminal args object (undefined kind = all reactions);
  // cursor rides pageParam, never the key.
  reactions: (target: ReactionTarget, args?: { kind?: ReactionKind }) =>
    infiniteQueryOptions({
      queryKey: [
        ...feedKeys.all,
        'reactions',
        target.kind,
        target.id,
        { kind: args?.kind },
      ] as const,
      queryFn: ({ pageParam }) =>
        fetchFeedReactions({
          target,
          ...(args?.kind !== undefined ? { kind: args.kind } : {}),
          cursor: (pageParam as string | undefined) ?? undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),
};

// Predicate for save/unsave cache invalidation. Co-located with FeedListParams
// so the discriminant moves with the filter shape — see the analogous helper
// in queries/resources.ts for why this lives next to the key builder rather
// than in the mutations file.
export function isSavedScopeFeedListKey(key: readonly unknown[]): boolean {
  if (key[0] !== 'feed' || key[1] !== 'list') return false;
  const params = key[2] as FeedListParams | undefined;
  return params?.scope === 'saved';
}

QUERY_KEY_REGISTRY.push(
  { name: 'feedKeys.list', sampleKey: feedKeys.list({ scope: 'all', pageSize: 20 }).queryKey },
  { name: 'feedKeys.detail', sampleKey: feedKeys.detail('post_1').queryKey },
  { name: 'feedKeys.scopeCounts', sampleKey: feedKeys.scopeCounts().queryKey },
  { name: 'feedKeys.authorPosts', sampleKey: feedKeys.authorPosts('user_1', 10).queryKey },
  { name: 'feedKeys.authorPostsInfinite', sampleKey: feedKeys.authorPostsInfinite('user_1', { pageSize: 10 }).queryKey },
  { name: 'feedKeys.comments', sampleKey: feedKeys.comments('post_1').queryKey },
  { name: 'feedKeys.commentDetail', sampleKey: feedKeys.commentDetail('comment_1').queryKey },
  { name: 'feedKeys.commentReplies', sampleKey: feedKeys.commentReplies('comment_1').queryKey },
  { name: 'feedKeys.reactions', sampleKey: feedKeys.reactions({ kind: 'post', id: 'post_1' }, { kind: 'support' }).queryKey },
);

// ---- Hooks ----

export function useInfiniteFeed(
  params: FeedListParams,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({ ...feedKeys.list(params), ...(options ?? {}) });
}

export function useFeed(
  params: FeedListParams,
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchFeed>>,
      Error,
      Awaited<ReturnType<typeof fetchFeed>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  // Flat reader reusing the infinite list leaf's key (one cache entry shared
  // with `useInfiniteFeed`); supplies its own non-paginated queryFn. Group
  // detail pages read only the first page (`data.items`) but key under
  // feedKeys.list so post mutations' feedKeys.lists() invalidation reaches them.
  return useQuery({
    queryKey: feedKeys.list(params).queryKey,
    queryFn: () => fetchFeed(params),
    ...options,
  });
}

export function useFeedScopeCounts() {
  return useQuery(feedKeys.scopeCounts());
}

export function useFeedPost(postId: string) {
  return useQuery(feedKeys.detail(postId));
}

export function usePostsByAuthor(authorId: string, limit?: number) {
  return useQuery(feedKeys.authorPosts(authorId, limit));
}

export function useInfiniteAuthorPosts(
  authorId: string,
  args?: { pageSize?: number },
) {
  return useInfiniteQuery(feedKeys.authorPostsInfinite(authorId, args));
}

export function useFeedComments(postId: string) {
  return useInfiniteQuery(feedKeys.comments(postId));
}

export function useFeedComment(
  commentId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({ ...feedKeys.commentDetail(commentId), ...(options ?? {}) });
}

export function useFeedCommentReplies(
  commentId: string,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    ...feedKeys.commentReplies(commentId),
    ...(options ?? {}),
  });
}

export function useFeedReactions(
  target: ReactionTarget,
  args?: { kind?: ReactionKind; enabled?: boolean },
) {
  return useInfiniteQuery({
    ...feedKeys.reactions(
      target,
      args?.kind !== undefined ? { kind: args.kind } : {},
    ),
    enabled: args?.enabled ?? true,
  });
}
